// ============================================================
// The plate log - what each body plate on this screen heard and played
// (plan body-map G1)
// ============================================================
// The fuzzer's plate oracle (engine/fuzz/plates) asks whether a shown plate
// plays each new attack on its body once and never the one already there
// when it mounted. A play leaves nothing behind, so the plate notes here
// when it mounted, which impact was there, each impact it heard and each it
// played; useShellImpacts notes the impacts this screen heard only once
// expired (they never reach a plate). The fuzz tap reads it. Kept for the
// oracle's window.

import { BEAT_WINDOW_MS } from '../engine/fuzz/beats';
import type { PlateLog, PlateRecord } from '../engine/fuzz/plates';

/** A little longer than the oracle looks back: kept by time, not count. */
const KEEP_MS = BEAT_WINDOW_MS + 15_000;
/** At most this many ids per plate: more than its window ever holds. */
const IDS = 100;

interface Live extends PlateRecord {
  heard: string[];
  played: string[];
  /** The plays already noted: StrictMode runs an effect twice for one play, which is not a replay. */
  noted: WeakSet<object>;
}

const plates = new Map<string, Live>();
/** id -> when heard expired; a Map keeps insertion order, which is time order. */
const stale = new Map<string, number>();

function prune(now: number): void {
  for (const [key, p] of plates) if (p.until !== null && now - p.until > KEEP_MS) plates.delete(key);
  for (const [id, at] of stale) {
    if (now - at <= KEEP_MS) break;
    stale.delete(id);
  }
}

const push = (ids: string[], id: string) => {
  ids.push(id);
  if (ids.length > IDS) ids.shift();
};

/** A plate of `combatantId` came up, `atMount` the impact already there (history). */
export function notePlateMounted(key: string, combatantId: string, atMount: string | null, now = Date.now()): void {
  prune(now);
  plates.set(key, { combatantId, since: now, until: null, atMount, heard: [], played: [], noted: new WeakSet() });
}

/** The plate went. */
export function notePlateGone(key: string, now = Date.now()): void {
  const p = plates.get(key);
  if (p) p.until = now;
}

/** The plate was handed an impact after it mounted. */
export function notePlateHeard(key: string, id: string): void {
  const p = plates.get(key);
  if (p && !p.heard.includes(id)) push(p.heard, id);
}

/** The plate began to play an impact; `play` is the play itself, so one play noted twice counts once. */
export function notePlatePlayed(key: string, id: string, play: object): void {
  const p = plates.get(key);
  if (!p || p.noted.has(play)) return;
  p.noted.add(play);
  push(p.played, id);
}

/** An impact this screen heard of only after it had expired: no plate owes it a play. */
export function notePlateStale(id: string, now = Date.now()): void {
  stale.delete(id);
  stale.set(id, now);
}

/** What the oracle reads (engine/fuzz/plates `PlateLog`). */
export function readPlateLog(): PlateLog {
  return {
    plates: [...plates.values()].map(({ noted: _noted, ...p }) => ({
      ...p,
      heard: [...p.heard],
      played: [...p.played],
    })),
    stale: [...stale.keys()],
    at: Date.now(),
  };
}
